/**
 * analysisHydration — rebuild the Dependencies / Impact / Test panels from a
 * loaded run's durable analysis events.
 *
 * During a live run the SSE handler (useRunStream) maps `dependencies_ready` /
 * `impact_analysis` / `test_coverage` / `test_run_completed` events into the IDE
 * store. Those events are persisted (multi_agent_run_events), and the run-detail
 * endpoint now returns them under `detail.analysis`. These mappers mirror the
 * SSE handlers EXACTLY so a past/loaded session shows the same panel data it had
 * live, instead of "No impact/test data yet".
 */
import {
  useMultiAgentIdeStore,
  type CoveredClass,
  type DependencyItem,
  type DependencySnapshot,
  type ImpactAnalysisEntry,
  type TestCoverageEntry,
  type TestRun,
} from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

export interface MultiAgentRunAnalysis {
  dependencies?: Array<Record<string, unknown>>;
  impact?: Array<Record<string, unknown>>;
  testCoverage?: Array<Record<string, unknown>>;
  testRuns?: Array<Record<string, unknown>>;
}

function obj(v: unknown): Record<string, unknown> {
  return (v ?? {}) as Record<string, unknown>;
}

function isoFromMs(ev: Record<string, unknown>): string {
  const ms = typeof ev.emitted_at_ms === "number" ? (ev.emitted_at_ms as number) : 0;
  return new Date(ms).toISOString();
}

export function mapDependenciesEvent(ev: Record<string, unknown>): DependencySnapshot | null {
  const toItems = (raw: unknown): DependencyItem[] =>
    (Array.isArray(raw) ? raw : [])
      .map((r) => {
        const o = obj(r);
        return {
          name: String(o.name ?? ""),
          id: String(o.id ?? ""),
          metadata_type: String(o.metadata_type ?? "ApexClass"),
        };
      })
      .filter((it) => it.name);
  const target = String(ev.target ?? "");
  if (!target) return null;
  const callers = toItems(ev.callers);
  const tests = toItems(ev.tests);
  return {
    target,
    target_id: (ev.target_id as string) ?? null,
    callers,
    tests,
    total: callers.length + tests.length,
    status: "awaiting_user_scope",
    captured_at: typeof ev.captured_at === "string" ? (ev.captured_at as string) : isoFromMs(ev),
  };
}

export function mapTestRunEvent(ev: Record<string, unknown>): TestRun | null {
  const className = String(ev.className ?? "");
  if (!className) return null;
  const status = String(ev.status ?? "");
  const success = status === "passed" || status === "Passed";
  const coveredClasses: CoveredClass[] = (Array.isArray(ev.coveredClasses) ? ev.coveredClasses : []).map((c) => {
    const o = obj(c);
    return {
      name: String(o.name ?? ""),
      coverage_pct: typeof o.coverage_pct === "number" ? (o.coverage_pct as number) : null,
      covered_lines: Array.isArray(o.covered_lines) ? (o.covered_lines as number[]) : [],
      uncovered_lines: Array.isArray(o.uncovered_lines) ? (o.uncovered_lines as number[]) : [],
      num_covered: Number(o.num_covered ?? 0),
      num_uncovered: Number(o.num_uncovered ?? 0),
    };
  });
  return {
    testClassName: className,
    className,
    coveredClasses,
    success,
    summary: success ? "Tests passed" : `Tests ${status || "failed"}`,
    ts: typeof ev.emitted_at_ms === "number" ? (ev.emitted_at_ms as number) : 0,
  };
}

export function mapImpactEvent(ev: Record<string, unknown>): ImpactAnalysisEntry | null {
  const className = String(ev.class_name ?? ev.className ?? "");
  if (!className) return null;
  const toCallers = (raw: unknown) =>
    (Array.isArray(raw) ? raw : []).map((r) => {
      const o = obj(r);
      const risk = String(o.risk ?? "low");
      return {
        name: String(o.name ?? ""),
        type: String(o.type ?? "ApexClass"),
        risk: (risk === "high" || risk === "medium" ? risk : "low") as "high" | "medium" | "low",
      };
    });
  const impact = (ev.impact && typeof ev.impact === "object" ? ev.impact : ev) as Record<string, unknown>;
  return {
    class_name: className,
    blast_radius: typeof impact.blast_radius === "number" ? (impact.blast_radius as number) : undefined,
    callers: toCallers(impact.callers),
    callees: (Array.isArray(impact.callees) ? impact.callees : []).map((c) => {
      const o = obj(c);
      return { name: String(o.name ?? ""), type: String(o.type ?? "") };
    }),
    data_access: (Array.isArray(impact.data_access) ? impact.data_access : []).map((d) => {
      const o = obj(d);
      return {
        object: String(o.object ?? ""),
        operations: Array.isArray(o.operations) ? (o.operations as string[]) : [],
      };
    }),
    triggers_at_risk: (Array.isArray(impact.triggers_at_risk) ? impact.triggers_at_risk : []).map((t) => {
      const o = obj(t);
      return { trigger: String(o.trigger ?? ""), object: String(o.object ?? "") };
    }),
    references: (Array.isArray(impact.references) ? impact.references : []).map((r) => {
      const o = obj(r);
      return { file: String(o.file ?? ""), line: Number(o.line ?? 0), kind: String(o.kind ?? "") };
    }),
    summary: typeof impact.summary === "string" ? (impact.summary as string) : undefined,
  };
}

export function mapTestCoverageEvent(ev: Record<string, unknown>): TestCoverageEntry | null {
  const className = String(ev.class_name ?? ev.className ?? "");
  if (!className) return null;
  const coverage = (ev.coverage && typeof ev.coverage === "object" ? ev.coverage : ev) as Record<string, unknown>;
  return {
    class_name: className,
    test_classes: (Array.isArray(coverage.test_classes) ? coverage.test_classes : []).map((t) => {
      const o = obj(t);
      return {
        name: String(o.name ?? ""),
        coverage_pct: typeof o.coverage_pct === "number" ? (o.coverage_pct as number) : null,
        lines_covered: typeof o.lines_covered === "number" ? (o.lines_covered as number) : null,
        lines_uncovered: typeof o.lines_uncovered === "number" ? (o.lines_uncovered as number) : null,
      };
    }),
    aggregate_coverage_pct:
      typeof coverage.aggregate_coverage_pct === "number" ? (coverage.aggregate_coverage_pct as number) : null,
    summary: typeof coverage.summary === "string" ? (coverage.summary as string) : undefined,
  };
}

/**
 * Replay a loaded run's persisted analysis into the IDE store. Uses the upsert
 * `add*` setters (dedupe by key, same as the live path), so it's safe to call
 * after a session switch (which clears the panels first).
 */
export function hydrateMultiAgentAnalysis(analysis: MultiAgentRunAnalysis | null | undefined): void {
  if (!analysis) return;
  const ide = useMultiAgentIdeStore.getState();
  for (const p of analysis.dependencies || []) {
    const e = mapDependenciesEvent(obj(p));
    if (e) ide.addDependency(e);
  }
  for (const p of analysis.impact || []) {
    const e = mapImpactEvent(obj(p));
    if (e) ide.addImpactAnalysis(e);
  }
  for (const p of analysis.testCoverage || []) {
    const e = mapTestCoverageEvent(obj(p));
    if (e) ide.addTestCoverage(e);
  }
  for (const p of analysis.testRuns || []) {
    const e = mapTestRunEvent(obj(p));
    if (e) ide.addTestRun(e);
  }
}
